// Koppelingen (planner), met de markup van het prototype (Koppelingen in design/build/prototype.js,
// proto-koppelingen-logic.js): wat in Planner, Clockify, de supportmailbox en Entra bij elkaar hoort. Vier tabbladen:
// Taken (Planner-taak en Clockify-taak), Klantplannen (klantplan en Clockify-project), Maildomeinen (afzenderdomein en
// klant) en Personen (een gastaccount of tweede Planner-resource telt na samenvoegen als de persoon). De app bewaart
// elke koppeling zelf, met naam en tijd. Naast de tabbladen de stand van de bronnen (Planner, Clockify, de
// supportmailbox); rechts de dekking per soort en de laatste wijzigingen.
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { KoppelDomeinen } from "../components/KoppelDomeinen";
import { KoppelCard, KoppelList, OFF, OK, WARN, type KRow } from "../components/KoppelList";
import { KoppelPlannen } from "../components/KoppelPlannen";
import { KoppelTaken } from "../components/KoppelTaken";
import { useClockifyLinks, type ClockifyStatus } from "../lib/api-clockify";
import { settingsApi, type AuditRow } from "../lib/api-settings";
import { supportApi, useDomainLinks, type SupportSync } from "../lib/api-support";
import { nOf, teamsApi, usePersonLinks, useTeamWrite, whenText, type LinksData, type Resource } from "../lib/api-teams";
import { useMe, usePlanning } from "../lib/hooks";
import { Icon } from "../lib/icons";
import { firstName } from "../lib/planning";
import type { PlanningData } from "../lib/types";
import { LoadError } from "../shell/Layout";

type Tab = "taken" | "plannen" | "domeinen" | "personen";
const TABS: [Tab, string][] = [["taken", "Taken"], ["plannen", "Klantplannen"], ["domeinen", "Maildomeinen"], ["personen", "Personen"]];
const KEYS = [["person-links"]];
/** in het logboek: koppelingen met Clockify (geen schattingen of timers) */
const CLOCKIFY_LINKS = ["clockify.plan", "clockify.task", "clockify.create"];

// zelfde naam: zonder "# ", punten en underscores als spatie, in elke volgorde
const norm = (s: string) => s.replace(/^#\s*/, "").replace(/[._]/g, " ").toLowerCase().split(/\s+/).filter(Boolean).sort().join(" ");
const keysOf = (r: Resource) => [...new Set([norm(r.name), norm(r.account)].filter(Boolean))];
const acctText = (r: Resource) => (r.guest ? "gastaccount" : !r.enabled ? "account uitgeschakeld" : "lid");

/** De personen over Entra en Planner: per groep met dezelfde naam één persoon, de rest is dubbel of gekoppeld. */
function analyse(res: LinksData, data: PlanningData) {
  const link = new Map(res.links.map((l) => [l.aliasId, l.personId]));
  const users = res.resources.filter((r) => r.personId);
  const own = users.filter((r) => !link.has(r.personId!));
  const asg = new Map<string, number>(), teams = new Map<string, number>();
  for (const t of data.tasks) for (const o of t.owners) asg.set(o.resourceId, (asg.get(o.resourceId) || 0) + 1);
  for (const m of data.team) teams.set(m.resourceId, (teams.get(m.resourceId) || 0) + 1);
  const people = new Map(data.people.map((p) => [p.id, p]));
  const nameOf = (pid: string) => people.get(pid)?.name || users.find((r) => r.personId === pid)?.account || users.find((r) => r.personId === pid)?.name || "Onbekend";
  const score = (r: Resource) => (r.enabled && !r.guest ? 4 : 0) + (r.name.startsWith("#") ? 0 : 2) + (people.has(r.personId!) ? 1 : 0);

  // groepen: resources die een naam delen (Planner-resource of Entra-account)
  const byKey = new Map<string, Resource[]>();
  for (const r of own) for (const k of keysOf(r)) byKey.set(k, [...(byKey.get(k) || []), r]);
  const seen = new Set<string>(), groups: Resource[][] = [];
  for (const r of own) {
    if (seen.has(r.id)) continue;
    const g: Resource[] = [], todo = [r];
    seen.add(r.id);
    while (todo.length) {
      const x = todo.pop()!;
      g.push(x);
      for (const k of keysOf(x)) for (const y of byKey.get(k) || []) if (!seen.has(y.id)) { seen.add(y.id); todo.push(y); }
    }
    g.sort((a, b) => score(b) - score(a) || (asg.get(b.id) || 0) - (asg.get(a.id) || 0) || a.name.localeCompare(b.name));
    groups.push(g);
  }
  const persons = groups.map((g) => ({ main: g[0], extra: g.filter((r) => r !== g[0] && r.personId === g[0].personId), dups: g.filter((r) => r.personId !== g[0].personId) }))
    .sort((a, b) => (a.main.account || a.main.name).localeCompare(b.main.account || b.main.name));
  const aliases = users.filter((r) => link.has(r.personId!));
  // wie een persoon kan zijn: een lid met een Planner-resource dat zelf niet gekoppeld is
  const targets = persons.filter((p) => p.main.enabled && !p.main.guest).map((p) => ({ id: p.main.personId!, name: p.main.account || p.main.name, keys: keysOf(p.main) }));
  const nTodo = persons.filter((p) => p.dups.length && p.main.enabled && !p.main.guest).length;
  return { link, own, persons, aliases, generic: res.resources.filter((r) => !r.personId), asg, teams, nameOf, targets, nTodo };
}
type Analysis = ReturnType<typeof analyse>;

/** Tekst van een koppeling van een maildomein in het logboek (de server bewaart daar geen tekst bij). */
function domainText(x: AuditRow): string {
  const d = String(x.details.domain || x.target), klant = x.details.klant ? String(x.details.klant) : "";
  if (x.action === "domain.unlink") return d + " ontkoppeld: weer een onbekend domein";
  return klant ? d + " gekoppeld aan klant " + klant : d + " genegeerd: telt niet mee per klant";
}

const hhmm = (at: string | number) => new Date(at).toLocaleTimeString("nl-BE", { timeZone: "Europe/Brussels", hour: "2-digit", minute: "2-digit" });
/** "10:14" vandaag, anders "gisteren"-loos "11/10 16:40" */
const at = (iso: string | null, today: string) => (iso ? whenText(iso, today).replace(/^vandaag /, "") : "–");

/** Eén bron naast de tabbladen: stip, naam en kort de stand (het uur van de laatste lezing), de uitleg als tip. */
interface Source { name: string; tone: "" | "ok" | "warn" | "err"; short: string; long: string }

function clockifySource(s: ClockifyStatus | undefined, today: string): Source {
  const name = "Clockify";
  if (!s) return { name, tone: "", short: "…", long: "" };
  if (s.state === "uit") return { name, tone: "warn", short: "niet gekoppeld", long: "Niet gekoppeld: de server heeft geen sleutel voor Clockify" };
  if (s.state === "wacht") return { name, tone: "warn", short: "eerste lezing volgt", long: "Gekoppeld; de eerste lezing volgt binnen enkele minuten" };
  if (s.state === "fout") {
    const e = (s.errorKind === "toegang" ? "Geen toegang: " : "Fout: ") + s.error;
    return { name, tone: "err", short: s.errorKind === "toegang" ? "geen toegang" : "fout", long: e + (s.lastOk ? " · laatst gelukt " + at(s.lastOk, today) : "") };
  }
  return {
    name, tone: "ok", short: at(s.lastOk, today),
    long: "Uren, taken, verlof en feestdagen bijgewerkt " + at(s.lastOk, today) + (s.timersAt ? " · timers " + hhmm(s.timersAt) : "") + " · " + nOf(s.users, "Clockify-gebruiker", "Clockify-gebruikers"),
  };
}

function mailSource(s: SupportSync | undefined, today: string): Source {
  const name = "Supportmailbox";
  if (!s) return { name, tone: "", short: "…", long: "" };
  const mb = s.mailbox || "de supportmailbox";
  if (s.state === "uit") return { name, tone: "", short: "staat uit", long: "De synchronisatie met de supportmailbox staat uit" };
  if (s.state === "toegang") return { name, tone: "warn", short: "geen leestoegang", long: "De app heeft nog geen leestoegang tot " + mb };
  if (s.state === "nooit") return { name, tone: "warn", short: "eerste lezing volgt", long: "De eerste lezing van " + mb + " volgt binnen enkele minuten" };
  if (s.state === "fout") return { name, tone: "err", short: "fout", long: mb + " · fout: " + s.error + (s.lastOk ? " · laatst gelukt " + at(s.lastOk, today) : "") };
  return { name, tone: "ok", short: at(s.lastOk, today), long: mb + " bijgewerkt " + at(s.lastOk, today) + " · alleen metadata" };
}

export function Koppelingen() {
  const { data, error, refetch, dataUpdatedAt } = usePlanning();
  const { data: me } = useMe();
  const planner = me?.role === "planner";
  const lq = usePersonLinks(planner);
  const cq = useClockifyLinks(planner);
  const dq = useDomainLinks(planner);
  // zelfde gegevens als Support (alle klanten): enkel voor de stand van de mailbox
  const sq = useQuery({ queryKey: ["support", null], queryFn: () => supportApi.overview(null), enabled: planner, staleTime: 30e3, refetchInterval: 60e3 });
  const aq = useQuery({ queryKey: ["audit", "domain."], queryFn: () => settingsApi.audit(8, "domain."), enabled: planner, refetchInterval: 60e3 });
  const [tab, setTab] = useState<Tab>("taken");
  const [q, setQ] = useState("");
  const a = useMemo(() => (lq.data && data ? analyse(lq.data, data) : null), [lq.data, data]);

  if (me && !planner) {
    return (
      <>
        <div className="p-head"><div><h1>Koppelingen</h1><p>Voor planners</p></div></div>
        <div className="ex-notice ex-notice--mist p-notice"><Icon name="info" size={16} /><p>Koppelingen beheert de planner.</p></div>
      </>
    );
  }
  if (!data && error) return <LoadError what="De planningsdata" onRetry={() => refetch()} />;
  if (!data || !me) return <div className="p-loading">Koppelingen laden…</div>;

  const go = (t: Tab) => { setTab(t); setQ(""); };
  const links = cq.data;

  // te doen per tabblad
  const openTasks = links ? links.tasks.filter((r) => !r.linked && !r.none && r.category !== "afsluiten").length : 0;
  const todo: Record<Tab, number> = {
    taken: openTasks,
    plannen: links ? links.plans.filter((p) => !p.linked).length : 0,
    domeinen: dq.data ? dq.data.unknown.length : 0,
    personen: a ? a.nTodo : 0,
  };

  // dekking: wat gekoppeld is van wat gekoppeld moet worden
  const need = links ? links.tasks.filter((r) => r.linked || (!r.none && r.category !== "afsluiten")) : [];
  const conv = dq.data ? (() => {
    const linked = dq.data.links.filter((l) => l.projectId).reduce((s, l) => s + l.conversations, 0);
    const other = dq.data.links.filter((l) => !l.projectId).reduce((s, l) => s + l.conversations, 0) + dq.data.unknown.reduce((s, u) => s + u.conversations, 0);
    return [linked, linked + other] as const;
  })() : null;
  const cover: { key: Tab; label: string; n: number | null; of: number; note: string }[] = [
    { key: "taken", label: "Taken met een Clockify-taak", n: links ? need.filter((r) => r.linked).length : null, of: need.length, note: "alleen deze tellen mee in uren en bezetting" },
    { key: "plannen", label: "Klantplannen met een project", n: links ? links.plans.filter((p) => p.linked).length : null, of: links?.plans.length || 0, note: "nodig om taken te koppelen" },
    { key: "domeinen", label: "Supportgesprekken met een klant", n: conv ? conv[0] : null, of: conv ? conv[1] : 0, note: "via het afzenderdomein" },
    { key: "personen", label: "Personen zonder dubbel account", n: a ? a.targets.length - a.nTodo : null, of: a ? a.targets.length : 0, note: "Entra en Planner" },
  ];

  // bronnen
  const failed = (src: Source): Source => ({ ...src, tone: "warn", short: "kon niet laden", long: "De stand kon niet laden" });
  const sources: Source[] = [
    { name: "Planner", tone: "ok", short: hhmm(dataUpdatedAt),
      long: "Planner Premium bijgewerkt " + hhmm(dataUpdatedAt) + " · " + nOf(data.tasks.length, "open taak", "open taken") + " in " + nOf(data.klanten.length, "klantplan", "klantplannen") },
    cq.error && !links ? failed(clockifySource(undefined, data.today)) : clockifySource(links?.clockify, data.today),
    sq.error && !sq.data ? failed(mailSource(undefined, data.today)) : mailSource(sq.data?.sync, data.today),
  ];

  // laatste wijzigingen over alle soorten koppelingen
  const log = [
    ...(lq.data?.log || []).map((x) => ({ key: "p" + x.id, text: x.text, at: x.at, who: x.who })),
    ...(links?.log || []).filter((x) => x.text && CLOCKIFY_LINKS.includes(x.action)).map((x) => ({ key: "c" + x.id, text: x.text, at: x.at, who: x.user })),
    ...(aq.data || []).map((x) => ({ key: "d" + x.id, text: domainText(x), at: x.at, who: x.userName })),
  ].sort((x, y) => Date.parse(y.at) - Date.parse(x.at)).slice(0, 8);

  const waiting = (title: string, what: string, qr: { error: unknown; refetch: () => unknown }) => (
    <KoppelCard title={title} sub="" foot="De app bewaart elke koppeling zelf.">
      <div className="p-kp-list">{qr.error ? <LoadError what={what} onRetry={() => { qr.refetch(); }} /> : <div className="p-loading">{what} laden…</div>}</div>
    </KoppelCard>
  );
  const main = tab === "taken" ? (links ? <KoppelTaken q={q} links={links} data={data} /> : waiting("Taken en Clockify-taken", "De koppelingen met Clockify", cq))
    : tab === "plannen" ? (links ? <KoppelPlannen q={q} links={links} data={data} /> : waiting("Klantplannen en Clockify-projecten", "De koppelingen met Clockify", cq))
    : tab === "domeinen" ? <KoppelDomeinen q={q} klanten={data.klanten} mailbox={sq.data?.sync.mailbox || "de supportmailbox"} today={data.today} />
    : a ? <Personen q={q} a={a} /> : waiting("Personen in Entra en Planner", "De Planner-resources", lq);

  return (
    <>
      <div className="p-head">
        <div><h1>Koppelingen</h1><p>Wat in Planner, Clockify, de supportmailbox en Entra bij elkaar hoort · voor planners · elke wijziging wordt bijgehouden</p></div>
        <div className="p-tools"><label className="ex-search p-search"><Icon name="search" size={15} /><input type="text" placeholder="Zoek in de koppelingen" aria-label="Zoek in de koppelingen" value={q} onChange={(e) => setQ(e.target.value)} /></label></div>
      </div>
      <div className="p-tabrow">
        <div className="ex-tabs p-tabs" role="tablist">
          {TABS.map(([k, label]) => (
            <button key={k} type="button" role="tab" className="ex-tab" aria-selected={tab === k} onClick={() => go(k)}>
              {label}{todo[k] > 0 && <span className="p-tabn">{todo[k]}</span>}
            </button>
          ))}
        </div>
        <div className="p-tools p-kp-srcs" role="group" aria-label="Bronnen">
          {sources.map((x) => (
            <span key={x.name} className="p-kp-src" title={x.long || undefined} aria-label={x.name + ": " + (x.long || x.short)}>
              <span className={"ex-dot" + (x.tone ? " ex-dot--" + x.tone : "")} />{x.name}<b className={x.tone === "err" ? "t-err" : x.tone === "warn" ? "t-warn" : ""}>{x.short}</b>
            </span>
          ))}
        </div>
      </div>
      <div className="p-kp">
        {main}
        <div className="p-kp-side">
          <section className="p-card">
            <div className="p-card-h"><div><h2>Dekking</h2><p>Klik om naar het tabblad te gaan</p></div></div>
            <div className="p-kp-covs">
              {cover.map((c) => {
                const pct = c.n != null && c.of ? Math.round((c.n / c.of) * 100) : c.n != null ? 100 : 0;
                return (
                  <button key={c.key} type="button" className={"p-kp-cov" + (tab === c.key ? " on" : "")} onClick={() => go(c.key)}>
                    <span className="l"><b>{c.label}</b><span>{c.n != null ? c.n + " van " + c.of : "…"}</span></span>
                    <span className="p-kp-bar"><i style={{ width: pct + "%" }} /></span>
                    <span className="n"><b>{c.n != null ? pct + " %" : "…"}</b> · {c.note}</span>
                  </button>
                );
              })}
            </div>
          </section>
          <section className="p-card p-kp-log">
            <div className="p-card-h"><div><h2>Laatste wijzigingen</h2><p>Elke koppeling met naam en tijd</p></div></div>
            <div className="p-kp-log-b">
              {log.map((x) => <div key={x.key} className="p-kp-log-i"><b>{x.text}</b><span>{whenText(x.at, data.today)} · {x.who}</span></div>)}
              {!log.length && <p className="p-empty p-tm-empty">Nog geen wijzigingen.</p>}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

/** Tabblad Personen: dubbele of gastaccounts samenvoegen tot één persoon. */
function Personen({ q, a }: { q: string; a: Analysis }) {
  const write = useTeamWrite();
  const [pick, setPick] = useState<Resource | null>(null);

  const work = (r: Resource) => nOf(a.asg.get(r.id) || 0, "open taak", "open taken");
  const merge = (aliasIds: string[], personId: string, label: string) => write.run({
    run: async () => {
      let last = { ok: true, text: "" };
      for (const id of aliasIds) last = await teamsApi.link(id, personId);
      return aliasIds.length > 1 ? { ...last, text: "Samengevoegd: " + label + " horen nu bij " + a.nameOf(personId) } : last;
    },
    undo: async () => { for (const id of aliasIds) await teamsApi.unlink(id); },
    keys: KEYS,
  }).catch(() => {});
  const unlink = (aliasId: string, personId: string) => write.run({ run: () => teamsApi.unlink(aliasId), undo: () => teamsApi.link(aliasId, personId), keys: KEYS }).catch(() => {});

  const qq = q.trim().toLowerCase();
  const match = (...s: string[]) => !qq || s.join(" ").toLowerCase().includes(qq);
  const todo: KRow[] = [], guests: KRow[] = [], merged: KRow[] = [], done: KRow[] = [], none: KRow[] = [];
  for (const p of a.persons) {
    const m = p.main, mName = m.account || m.name;
    if (!match(mName, m.name, ...p.dups.map((d) => d.name + " " + d.account))) continue;
    if (p.dups.length && m.enabled && !m.guest) {
      const d = p.dups[0], ids = [...new Set(p.dups.map((x) => x.personId!))];
      const dupNames = p.dups.map((x) => x.name).join(", ");
      todo.push({
        key: m.id, a: mName, aSub: acctText(m) + " · " + work(m), b: "Twee Planner-resources", bSub: "ook " + d.name + ", " + (d.guest ? "gast" : !d.enabled ? "uitgeschakeld" : "tweede") + " · " + nOf(a.teams.get(d.id) || 0, "projectteam", "projectteams") + " · " + work(d), bSubCls: "t-warn",
        pill: "Dubbel", pillCls: WARN, acts: [{ label: "Samenvoegen", primary: true, run: () => merge(ids, m.personId!, dupNames) }, { label: "Andere", run: () => setPick(d) }],
      });
    } else if (m.guest || !m.enabled) {
      const row: KRow = { key: m.id, a: mName, aSub: acctText(m) + " · " + work(m), b: "Telt niet mee", bSub: "koppel het aan een collega als het dezelfde persoon is", pill: m.guest ? "Gast" : "Uit", pillCls: OFF, acts: [{ label: "Koppelen", run: () => setPick(m) }] };
      (m.guest ? guests : none).push(row);
      for (const d of p.dups) (d.guest ? guests : none).push({ ...row, key: d.id, a: d.account || d.name, aSub: acctText(d) + " · " + work(d), acts: [{ label: "Koppelen", run: () => setPick(d) }] });
    } else {
      const extra = p.extra.length ? " · " + (p.extra.length + 1) + " resources" : "";
      done.push({ key: m.id, a: mName, aSub: acctText(m) + " · " + work(m), b: m.name, bSub: "Planner-resource" + extra + " · " + nOf(a.teams.get(m.id) || 0, "projectteam", "projectteams"), pill: "Gekoppeld", pillCls: OK });
    }
  }
  for (const r of a.aliases) {
    const to = a.link.get(r.personId!)!;
    if (!match(r.name, r.account, a.nameOf(to))) continue;
    merged.push({ key: r.id, a: r.account || r.name, aSub: acctText(r) + " · " + r.name, b: a.nameOf(to), bSub: "telt als " + firstName(a.nameOf(to)) + " op het bord · " + work(r), pill: "Samengevoegd", pillCls: OK, acts: [{ label: "Ontkoppelen", run: () => unlink(r.personId!, to) }] });
  }
  for (const r of a.generic) {
    if (!match(r.name)) continue;
    none.push({ key: r.id, a: r.name, aSub: "Planner-resource zonder account", b: "Genegeerd", bSub: "telt niet mee op het bord of in de capaciteit", pill: "Genegeerd", pillCls: OFF });
  }
  const rows: KRow[] = [...todo];
  const section = (head: string, list: KRow[]) => { if (list.length) rows.push({ key: "h:" + head, head }, ...list); };
  section("Gastaccounts", guests);
  section("Samengevoegd", merged);
  section("Gekoppeld", done);
  section("Geen persoon", none);

  return (
    <>
      <KoppelCard title="Personen in Entra en Planner" sub="Een Entra-account en zijn Planner-resources horen bij één persoon; een gast- of tweede account telt na samenvoegen als die persoon"
        foot="De app bewaart elke koppeling zelf; in Planner blijft alles staan. Het bord past zich binnen een minuut aan.">
        <KoppelList rows={rows} busy={write.busy} empty={qq ? "Niets gevonden voor " + q.trim() + "." : "Niets meer te doen in deze lijst."} />
      </KoppelCard>
      {pick && <PickDrawer key={pick.id} r={pick} a={a} write={write} onClose={() => setPick(null)} />}
    </>
  );
}

/** Zelf kiezen bij welke persoon een gast- of tweede account hoort. */
function PickDrawer({ r, a, write, onClose }: { r: Resource; a: Analysis; write: ReturnType<typeof useTeamWrite>; onClose: () => void }) {
  const cur = a.link.get(r.personId!) || null;
  const mine = keysOf(r);
  const options = a.targets.filter((t) => t.id !== r.personId)
    .map((t) => ({ ...t, same: t.keys.some((k) => mine.includes(k)) }))
    .sort((x, y) => Number(y.same) - Number(x.same) || x.name.localeCompare(y.name));
  const [sel, setSel] = useState<string | null>(cur || options[0]?.id || null);
  const [q, setQ] = useState("");
  const qq = q.trim().toLowerCase();
  const shown = options.filter((o) => !qq || o.name.toLowerCase().includes(qq));
  const confirm = () => {
    if (!sel || sel === cur) { onClose(); return; }
    write.run({
      run: () => teamsApi.link(r.personId!, sel),
      undo: () => (cur ? teamsApi.link(r.personId!, cur) : teamsApi.unlink(r.personId!)),
      keys: KEYS,
    }).then(onClose, () => {});
  };
  return (
    <>
      <button type="button" className="p-scrim" aria-label="Sluiten" onClick={onClose} />
      <aside className="p-drawer" role="dialog" aria-modal="true" aria-labelledby="kt">
        <div className="p-drawer-h">
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <div style={{ minWidth: 0 }}><p className="ex-eyebrow" style={{ margin: 0 }}>Persoon koppelen</p><h2 id="kt">{r.account || r.name}</h2></div>
            <button type="button" className="ex-icon-btn ex-icon-btn--square" aria-label="Sluiten" title="Sluiten" onClick={onClose}><Icon name="x" size={18} /></button>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
            <span className={cur ? OK : WARN}><span className="ex-dot" />{cur ? "Hoort bij " + a.nameOf(cur) : "Telt als eigen persoon"}</span>
          </div>
        </div>
        <div className="p-drawer-b" style={{ overflowY: "auto" }}>
          <div className="p-fs">
            {[["Entra", acctText(r)], ["Planner-resource", r.name], ["Open taken", String(a.asg.get(r.id) || 0)], ["Projectteams", String(a.teams.get(r.id) || 0)]].map(([k, v]) => (
              <div key={k} className="p-kv"><span>{k}</span><b>{v}</b></div>
            ))}
          </div>
          <div className="p-fs"><h3>Bij welke persoon hoort dit account?</h3>
            {options.length > 8 && <label className="ex-search p-kp-q"><Icon name="search" size={14} /><input type="text" placeholder="Zoek in de lijst" aria-label="Zoek in de lijst" value={q} onChange={(e) => setQ(e.target.value)} /></label>}
            <div className="p-capc">
              {shown.map((o) => (
                <button key={o.id} type="button" className={"p-cand p-kp-opt" + (sel === o.id ? " on" : "")} onClick={() => setSel(o.id)}>
                  <span className={"p-radio" + (sel === o.id ? " on" : "")} />
                  <span className="p-kp-opt-t"><b>{o.name}</b>{o.id === cur ? <span className="m">nu gekoppeld</span> : o.same && <span className="m">zelfde naam</span>}</span>
                  <span>{o.same && <span className={OK}>voorstel op naam</span>}</span>
                </button>
              ))}
              {!shown.length && <p className="p-empty">Niemand gevonden.</p>}
            </div>
            <p className="p-kd-note" style={{ margin: "12px 0 0" }}>Taken en projectteams van dit account tellen daarna voor de gekozen persoon. In Planner blijft alles staan.</p>
          </div>
        </div>
        <div className="p-drawer-f">
          <span className="t-mute" style={{ fontSize: 13 }}>De app bewaart de koppeling, met naam en tijd</span>
          <span style={{ display: "flex", gap: 8 }}>
            <button type="button" className="ex-btn" onClick={onClose}>Annuleren</button>
            <button type="button" className="ex-btn ex-btn--primary" disabled={write.busy || !sel} onClick={confirm}><Icon name="check" size={15} />Koppelen</button>
          </span>
        </div>
      </aside>
    </>
  );
}
